import type { DiktatHinweis } from "./useDiktat";

export type Browser = "edge" | "chrome" | "safari" | "firefox" | "anderer";

/** Edge nennt sich im User-Agent auch „Chrome“ — deshalb Edge zuerst prüfen. */
export function erkenneBrowser(ua: string = typeof navigator !== "undefined" ? navigator.userAgent : ""): Browser {
    if (/Edg\//.test(ua)) return "edge";
    if (/Firefox\//.test(ua)) return "firefox";
    if (/Chrome\//.test(ua) || /CriOS\//.test(ua)) return "chrome";
    if (/Safari\//.test(ua)) return "safari";
    return "anderer";
}

export interface HinweisText {
    titel: string;
    text: string;
    schritte?: string[];
}

const ERLAUBEN: Record<Browser, string[]> = {
    edge: [
        "Klicken Sie oben in der Adressleiste ganz links auf das Schloss-Symbol.",
        "Wählen Sie „Berechtigungen für diese Website“.",
        "Stellen Sie „Mikrofon“ auf „Zulassen“.",
        "Laden Sie die Seite neu (Taste F5) und klicken Sie noch einmal auf das Mikrofon.",
    ],
    chrome: [
        "Klicken Sie oben in der Adressleiste ganz links auf das Symbol vor der Adresse.",
        "Schalten Sie „Mikrofon“ ein bzw. wählen Sie „Zulassen“.",
        "Laden Sie die Seite neu (Taste F5) und klicken Sie noch einmal auf das Mikrofon.",
    ],
    safari: [
        "Öffnen Sie oben im Menü „Safari“ → „Einstellungen für diese Website“.",
        "Stellen Sie „Mikrofon“ auf „Erlauben“.",
        "Laden Sie die Seite neu und klicken Sie noch einmal auf das Mikrofon.",
    ],
    firefox: [
        "Klicken Sie oben in der Adressleiste auf das durchgestrichene Mikrofon-Symbol.",
        "Entfernen Sie die Sperre für das Mikrofon.",
        "Laden Sie die Seite neu und klicken Sie noch einmal auf das Mikrofon.",
    ],
    anderer: [
        "Erlauben Sie KMVS in den Website-Einstellungen Ihres Browsers den Zugriff auf das Mikrofon.",
        "Laden Sie die Seite danach neu.",
    ],
};

export function hinweisText(h: DiktatHinweis, browser: Browser = erkenneBrowser()): HinweisText {
    switch (h.art) {
        case "verweigert":
            return {
                titel: "Mikrofon ist gesperrt",
                text: "KMVS darf Ihr Mikrofon noch nicht benutzen. So geben Sie es frei:",
                schritte: ERLAUBEN[browser],
            };
        case "kein-mikrofon":
            return {
                titel: "Kein Mikrofon gefunden",
                text: "Am Computer ist kein Mikrofon angeschlossen oder es ist ausgeschaltet.",
                schritte: [
                    "Stecken Sie Ihr Headset an bzw. schalten Sie es ein.",
                    "In Windows: Start → Einstellungen → System → Sound → unter „Eingabe“ das Mikrofon auswählen.",
                    "Klicken Sie danach noch einmal auf das Mikrofon.",
                ],
            };
        case "belegt":
            return {
                titel: "Mikrofon lässt sich nicht öffnen",
                text: "Windows gibt das Mikrofon gerade nicht frei — oft benutzt es ein anderes Programm (z. B. Teams).",
                schritte: [
                    "Beenden Sie kurz das andere Programm oder dessen Anruf.",
                    "In Windows: Start → Einstellungen → Datenschutz und Sicherheit → Mikrofon → „Desktop-Apps den Zugriff auf Ihr Mikrofon erlauben“ einschalten.",
                    "Klicken Sie danach noch einmal auf das Mikrofon.",
                ],
            };
        case "nicht-unterstuetzt":
            return {
                titel: "Diktieren geht in diesem Browser nicht",
                text: "Dieser Browser kann keine Sprachaufnahme machen. Bitte öffnen Sie KMVS in Microsoft Edge.",
            };
        case "nichts-erkannt":
            return {
                titel: "Nichts verstanden",
                text: "In der Aufnahme war kein Text zu hören. Bitte etwas näher ans Mikrofon und noch einmal versuchen.",
            };
        default:
            return {
                titel: "Diktat hat nicht geklappt",
                text: h.meldung ?? "Das Diktat konnte nicht umgewandelt werden. Bitte noch einmal versuchen.",
            };
    }
}
